import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  UsersThreeIcon, EyeIcon, TrendUpIcon, ShoppingCartIcon,
  DiscordLogoIcon, TelegramLogoIcon, InstagramLogoIcon,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const CHANNEL_ICON = {
  discord: DiscordLogoIcon,
  telegram: TelegramLogoIcon,
  instagram: InstagramLogoIcon,
};
const CHANNEL_TINT = { discord: "#5865F2", telegram: "#22D3EE", instagram: "#E879F9" };

function Stat({ label, value, sub, color = "#F1F5F9", testid }) {
  return (
    <div data-testid={testid} className="border border-[#2A2D3A] bg-[#0F1116] rounded-md p-3">
      <div className="mono text-[9px] uppercase tracking-widest text-[#64748B]">{label}</div>
      <div className="mono text-lg mt-1" style={{ color }}>{value}</div>
      {sub && <div className="mono text-[9px] text-[#64748B] mt-0.5">{sub}</div>}
    </div>
  );
}

export default function StoreAnalyticsTile() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, []);

  if (err) {
    return (
      <div data-testid="store-analytics-error" className="border border-[#2A2D3A] bg-[#15171E] rounded-lg p-4 mono text-xs text-[#FF4D4D]">
        analytics unavailable: {err}
      </div>
    );
  }
  if (!data) {
    return (
      <div data-testid="store-analytics-loading" className="border border-[#2A2D3A] bg-[#15171E] rounded-lg p-4 mono text-xs text-[#64748B]">
        loading storefront analytics…
      </div>
    );
  }

  const t = data.traffic || {};
  const w = data.waitlist || {};
  const c = data.checkouts || {};

  return (
    <div data-testid="store-analytics-tile" className="border border-[#2A2D3A] bg-[#15171E] rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-[#2A2D3A] bg-[#0F1116] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <EyeIcon size={16} className="text-[#22D3EE]" />
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#22D3EE]">Storefront Analytics</div>
        </div>
        <div className="mono text-[9px] text-[#64748B]">
          {t.month?.uniques || 0} unique visitors · last 30d
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Traffic totals */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat testid="stat-visits-24h" label="Visits · 24h" value={t.day?.visits || 0}
                sub={`${t.day?.uniques || 0} unique`} color="#22D3EE" />
          <Stat testid="stat-visits-7d" label="Visits · 7d" value={t.week?.visits || 0}
                sub={`${t.week?.uniques || 0} unique`} color="#D6FF38" />
          <Stat testid="stat-visits-30d" label="Visits · 30d" value={t.month?.visits || 0}
                sub={`${t.month?.uniques || 0} unique`} color="#E879F9" />
          <Stat testid="stat-conversion" label="Conversion" value={`${c.conversion_pct || 0}%`}
                sub={`${c.total_paid || 0}/${c.total_initiated || 0} sessions`} color="#FBBF24" />
        </div>

        {/* Sparkline */}
        {t.sparkline_30d?.length > 0 && (
          <div className="border border-[#2A2D3A] bg-[#0F1116] rounded-md p-3">
            <div className="flex items-center justify-between">
              <div className="mono text-[9px] uppercase tracking-widest text-[#64748B]">Daily visits · 30d</div>
              <div className="mono text-[9px] text-[#64748B]">peak {Math.max(...t.sparkline_30d.map(d => d.visits))}/day</div>
            </div>
            <div className="h-24 mt-2">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={t.sparkline_30d} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="viewsFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor="#22D3EE" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#22D3EE" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#1F2230" strokeDasharray="2 4" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 8 }} axisLine={false} tickLine={false} interval={4} />
                  <YAxis tick={{ fill: "#64748B", fontSize: 9 }} axisLine={false} tickLine={false} width={30} />
                  <Tooltip contentStyle={{ background: "#0F1116", border: "1px solid #2A2D3A", fontSize: 11 }} labelStyle={{ color: "#94A3B8" }} />
                  <Area type="monotone" dataKey="visits" stroke="#22D3EE" strokeWidth={2} fill="url(#viewsFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Waitlist + Top pages side-by-side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Waitlist by channel */}
          <div className="border border-[#2A2D3A] bg-[#0F1116] rounded-md p-3">
            <div className="flex items-center gap-2">
              <UsersThreeIcon size={14} className="text-[#D6FF38]" />
              <div className="mono text-[9px] uppercase tracking-widest text-[#64748B]">
                Waitlist · {w.grand_total || 0} total signups
              </div>
            </div>
            <div className="mt-3 space-y-2">
              {(["discord", "telegram", "instagram"]).map((ch) => {
                const I = CHANNEL_ICON[ch];
                const total = w.totals?.[ch] || 0;
                const recent = w.last_7d?.[ch] || 0;
                return (
                  <div key={ch} data-testid={`waitlist-${ch}`} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <I size={14} style={{ color: CHANNEL_TINT[ch] }} />
                      <span className="mono text-xs capitalize text-[#F1F5F9]">{ch}</span>
                    </div>
                    <div className="mono text-xs">
                      <span className="text-[#F1F5F9]">{total}</span>
                      {recent > 0 && <span className="text-[#2ECA7F] ml-2">+{recent} 7d</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top pages */}
          <div className="border border-[#2A2D3A] bg-[#0F1116] rounded-md p-3">
            <div className="flex items-center gap-2">
              <TrendUpIcon size={14} className="text-[#E879F9]" />
              <div className="mono text-[9px] uppercase tracking-widest text-[#64748B]">Top pages · 30d</div>
            </div>
            <div className="mt-3 space-y-1.5">
              {(t.top_pages || []).length === 0 && (
                <div className="mono text-[10px] text-[#64748B]">No page views yet.</div>
              )}
              {(t.top_pages || []).map((p) => (
                <div key={p.path} className="flex items-center justify-between">
                  <span className="mono text-[11px] text-[#F1F5F9] truncate max-w-[60%]">{p.path}</span>
                  <span className="mono text-[11px] text-[#22D3EE]">{p.visits}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Checkout footer */}
        <div className="border border-[#2A2D3A] bg-[#0F1116] rounded-md p-3 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <ShoppingCartIcon size={14} className="text-[#FBBF24]" />
            <div className="mono text-[9px] uppercase tracking-widest text-[#64748B]">Checkout sessions</div>
          </div>
          <div className="mono text-[11px] text-[#F1F5F9]">
            <span>{c.total_initiated || 0} initiated</span>
            <span className="text-[#64748B]"> · </span>
            <span className="text-[#2ECA7F]">{c.total_paid || 0} paid</span>
            <span className="text-[#64748B]"> · </span>
            <span>{c.last7_initiated || 0} last 7d</span>
          </div>
        </div>
      </div>
    </div>
  );
}
